import { Component, HostListener, inject, OnInit } from '@angular/core';
import { AuthService } from './route/auth/auth.service';
import { SplitViewObserverService } from './shared/components/split-view/split-view-observer.service';
import { TranslateService } from '@ngx-translate/core';
import { AppConfig } from './appconfig';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, switchMap, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from './shared/dialogs/confirm-dialog/confirm-dialog.component';
import { ErrorDialogComponent } from './shared/dialogs/error-dialog/error-dialog.component';
import { SettingsService } from './shared/settings/settings.service';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

@Component({
    selector: 'ldw-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false
})
export class AppComponent implements OnInit {
  private authService = inject(AuthService);
  private splitViewObserver = inject(SplitViewObserverService);
  private swUpdate = inject(SwUpdate);
  private settingsService = inject(SettingsService);
  private appCfg = inject(AppConfig);

  @HostListener('window:resize', ['$event'])
  notifyMoved(event: any) {
    this.splitViewObserver.notifyMoved({ gutterNumber: 0 });
  }

  @HostListener('beforeinstallprompt', ['$event'])
  promtPWAInstall(event: BeforeInstallPromptEvent) {
    event.preventDefault();
    if (this.appCfg.isMobileApp() || !this.settingsService.settings.miscSettings.enablePWA) {
      console.info("remove manifest (either mobile app or enablepwa is false ")
      document.querySelector('link[rel="manifest"]')?.remove()
      return;
    }
    event.userChoice.then(choice => {
      if (choice.outcome === 'dismissed') {
        console.info("setting enablepwa to false ")
        this.settingsService.settings.miscSettings.enablePWA = false;
      }
    })
    event.prompt();
  }

   protected translate = inject(TranslateService);
   private dialog = inject(MatDialog)

  ngOnInit(): void {
    const postLogoutRedirectUrl = localStorage.getItem('postLogoutRedirectUrl');
    localStorage.removeItem('postLogoutRedirectUrl')

    this.authService.runInitialLoginSequence(postLogoutRedirectUrl).then(() => {
      if(postLogoutRedirectUrl) {
        window.location.href = postLogoutRedirectUrl
      }
      this.initializeServiceWorker();
    });
    console.info("enablepwa is " + this.settingsService.settings.miscSettings.enablePWA)
    if(this.appCfg.isMobileApp() || !this.settingsService.settings.miscSettings.enablePWA) {
      console.info("removing manifest ... " )
      document.querySelector('link[rel="manifest"]')?.remove();
    }


  }

  initializeServiceWorker() {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.pipe(
        filter((evt: any): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        switchMap((evt: any) => {
          console.info(`currentVersion=[${JSON.stringify(evt.currentVersion)} | latestVersion=[${JSON.stringify(evt.latestVersion)}]`);
          return this.dialog.open(ConfirmDialogComponent, {
            disableClose: true, closeOnNavigation: false, data: {
              title: 'Neue Version der Anwendung verfügbar',
              message2: 'soll die neue Version geladen werden?',
              isOkDefault: true
            }
          }).afterClosed();
        }),
        tap(result => {
          if (result) {
            window.location.reload();
          }
        })
      ).subscribe();

      this.swUpdate.unrecoverable.pipe(switchMap(() => this.dialog.open(ErrorDialogComponent, {
        disableClose: true,
        closeOnNavigation: false,
        data: { title: 'Fehler', message: 'Fehler in der Anwendung, sie wird neu geladen' }
      }).afterClosed()))
        .subscribe(event => {
          window.location.reload();
        });
    }
  }

}
